import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Col, Container, FormGroup, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader, Row,
  Spinner
} from "reactstrap";
import Handler from "../Helpers/Handler";
import Select from "react-select";
import Pagination from "../Helpers/Pagination";
import "boxicons/css/boxicons.min.css";

const Add = () => {

  /* ---------------- STATE ---------------- */
  const [loading, setLoading] = useState(false)
  const [category, setCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);

  const [catPage, setCatPage] = useState(1);
  const [subCatPage, setSubCatPage] = useState(1);

  const limit = 10;

  const [catTotal, setCatTotal] = useState(0);
  const [subCatTotal, setSubCatTotal] = useState(0);

  const [data, setData] = useState({});
  const [allCategory, setAllCategory] = useState([]);

  const [catModal, setCatModal] = useState(false);
  const [subCatModal, setSubCatModal] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);

  const [isEdit, setIsEdit] = useState(false);
  const [editType, setEditType] = useState(null);
  const [editId, setEditId] = useState(null);

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    setData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* ---------------- LOAD CATEGORY ---------------- */
  const loadCategories = async () => {
    const response = await Handler({
      method: "get",
      url: `/categories/categories_list.php?page=${catPage}&limit=${limit}`,
    });

    if (response?.success) {
      setCategory(response.data);
      setCatTotal(response.total);
      setCatPage(response.page);
    }
  };

  /* ---------------- LOAD SUB CATEGORY ---------------- */
  const loadSubCategories = async () => {
    const response = await Handler({
      method: "get",
      url: `/categories/sub_categories_list.php?page=${subCatPage}&limit=${limit}`,
    });

    if (response?.success) {
      setSubCategory(response.data);
      setSubCatTotal(response.total);
      setSubCatPage(response.page);
    }
  };

  /* ---------------- GET ALL CATEGORIES ---------------- */
  const getAllCategories = async () => {
    const response = await Handler({
      method: "get",
      url: `/categories/categories_list.php?page=1&limit=9999`,
    });

    if (response?.success) {
      setAllCategory(
        response.data.map(c => ({
          value: c.category_id,
          label: c.name
        }))
      );
    }
  };

  /* ---------------- SUBMIT CATEGORY ---------------- */
  const submitCategory = async () => {
    if (!data.category) return toast.error("Enter category");

    let url = "/categories/categories.php";
    let method = "post";
    let payload = { name: data.category };

    if (isEdit && editType === "category") {
      url = "/categories/categories_update.php";
      method = "patch";
      payload = { category_id: editId, name: data.category };
    }
    setLoading(true)
    const res = await Handler({ method, url, data: payload });
    if (res?.success) {
      setLoading(false)
      toast.success(isEdit ? "Updated" : "Added");
      resetModal();
      setCatModal(false);
      loadCategories();
      getAllCategories();
    } else {
      setLoading(false)
      toast.error(res?.message);
    }
  };

  /* ---------------- SUBMIT SUBCATEGORY ---------------- */
  const submitSubCategory = async () => {
    if (!selectedCategory) return toast.error("Select Category");
    if (!data.subcategory) return toast.error("Enter Sub Category");
    setLoading(true)
    let url = "/categories/sub_categories.php";
    let method = "post";
    let payload = {
      name: data.subcategory,
      category_id: selectedCategory.value
    };
    if (isEdit && editType === "subcategory") {
      url = "/categories/sub_cat_update.php";
      method = "patch";
      payload = {
        subcategory_id: editId,
        name: data.subcategory
      };
    }
    const res = await Handler({ method, url, data: payload });
    if (res?.success) {
      setLoading(false)
      toast.success(isEdit ? "Updated" : "Added");
      resetModal();
      setSubCatModal(false);
      loadSubCategories();
    } else {
      setLoading(false)
      toast.error(res?.message);
    }
  };

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => { loadCategories(); }, [catPage]);
  useEffect(() => { loadSubCategories(); }, [subCatPage]);
  useEffect(() => { getAllCategories(); }, []);

  /* ---------------- RESET MODAL ---------------- */
  const resetModal = () => {
    setIsEdit(false);
    setEditType(null);
    setEditId(null);
    setData({});
    setSelectedCategory(null);
    setLoading(false)
  };

  return (
    <div className="dashboard-wrapper py-3 py-md-4">
      {/* Custom Style for Mobile Header Spacing */}
      <style>{`
        @media (max-width: 576px) {
          .section-header-flex {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .btn-add {
            width: 100%;
            justify-content: center;
            display: flex;
            align-items: center;
          }
          .dash-card {
            padding: 15px !important;
          }
        }
      `}</style>

      <Container fluid="lg">
        <Row className="g-4">

          {/* ===================== CATEGORY LIST ===================== */}
          <Col lg={6} md={12}>
            <div className="dash-card p-4 shadow-sm bg-white rounded">
              <div className="d-flex justify-content-between align-items-center mb-3 section-header-flex">
                <h5 className="mb-0 fw-bold">
                    <i className="bx bxs-folder-open me-2 text-primary"></i> Category List
                </h5>
                <Button className="btn-add" onClick={() => { resetModal(); setCatModal(true); }}>
                  <i className="bx bx-plus me-1"></i> Add Category
                </Button>
              </div>

              <div className="table-responsive">
                <table className="table modern-table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}>#</th>
                      <th>Category Name</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.length ? category.map((item, i) => (
                      <tr key={item.category_id}>
                        <td>{(catPage - 1) * limit + i + 1}</td>
                        <td className="text-capitalize">{item.name}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-dark rounded-pill px-3 shadow-sm"
                            onClick={() => {
                              setIsEdit(true);
                              setEditType("category");
                              setEditId(item.category_id);
                              setData({ category: item.name });
                              setCatModal(true);
                            }}
                          >
                            <i className="bx bx-edit me-1"></i> Edit
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="3" className="text-center py-4 text-muted">No Categories Found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 d-flex justify-content-center">
                <Pagination
                  page={catPage}
                  total={catTotal}
                  limit={limit}
                  onPrev={() => setCatPage(p => p - 1)}
                  onNext={() => setCatPage(p => p + 1)}
                />
              </div>
            </div>
          </Col>

          {/* ===================== SUB CATEGORY LIST ===================== */}
          <Col lg={6} md={12}>
            <div className="dash-card p-4 shadow-sm bg-white rounded">
              <div className="d-flex justify-content-between align-items-center mb-3 section-header-flex">
                <h5 className="mb-0 fw-bold">
                    <i className="bx bxs-purchase-tag-alt me-2 text-primary"></i> Sub Categories
                </h5>
                <Button className="btn-add" onClick={() => { resetModal(); setSubCatModal(true); }}>
                  <i className="bx bx-plus me-1"></i> Add Sub
                </Button>
              </div>

              <div className="table-responsive">
                <table className="table modern-table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "50px" }}>#</th>
                      <th>Category</th>
                      <th>Sub Category</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subCategory.length ? subCategory.map((item, i) => (
                      <tr key={item.subcategory_id}>
                        <td>{(subCatPage - 1) * limit + i + 1}</td>
                        <td className="small text-muted">{item.category_name}</td>
                        <td className="fw-medium text-capitalize">{item.name}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-dark rounded-pill px-3 shadow-sm"
                            onClick={() => {
                              setIsEdit(true);
                              setEditType("subcategory");
                              setEditId(item.subcategory_id);
                              setSelectedCategory({
                                value: item.category_id,
                                label: item.category_name
                              });
                              setData({ subcategory: item.name });
                              setSubCatModal(true);
                            }}
                          >
                            <i className="bx bx-edit me-1"></i> Edit
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="text-center py-4 text-muted">No Subcategories Found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 d-flex justify-content-center">
                <Pagination
                  page={subCatPage}
                  total={subCatTotal}
                  limit={limit}
                  onPrev={() => setSubCatPage(p => p - 1)}
                  onNext={() => setSubCatPage(p => p + 1)}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* ===================== CATEGORY MODAL ===================== */}
      <Modal isOpen={catModal} toggle={() => { resetModal(); setCatModal(false); }} centered>
        <ModalHeader className="modal-head border-0 pb-0">
          <div className="d-flex align-items-center">
            <i className="bx bxs-category me-2 text-primary"></i>
            {isEdit && editType === "category" ? "Edit Category" : "Add Category"}
          </div>
        </ModalHeader>
        <ModalBody className="py-4">
          <FormGroup className="mb-0">
            <Label className="fw-bold small">Category Name</Label>
            <Input
              name="category"
              className="py-2"
              value={data.category || ""}
              onChange={handleChange}
              placeholder="e.g. Beverages, Electronics"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-0 pt-0">
          <Button color="light" className="px-4" onClick={() => { resetModal(); setCatModal(false); }}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            disabled={loading} 
            onClick={submitCategory} 
            className="px-4 d-flex align-items-center gap-2 shadow-sm"
          >
            {loading && <Spinner size="sm" color="light" />}
            {isEdit && editType === "category" ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ===================== SUB CATEGORY MODAL ===================== */}
      <Modal isOpen={subCatModal} toggle={() => { resetModal(); setSubCatModal(false); }} centered>
        <ModalHeader className="modal-head border-0 pb-0">
          <div className="d-flex align-items-center">
            <i className='bx bxs-purchase-tag-alt me-2 text-primary'></i>
            {isEdit && editType === "subcategory" ? "Edit Sub Category" : "Add Sub Category"}
          </div>
        </ModalHeader>
        <ModalBody className="py-4">
          <FormGroup className="mb-3">
            <Label className="fw-bold small">Select Parent Category</Label>
            <Select
              classNamePrefix="react-select"
              options={allCategory}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Search Category..."
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="fw-bold small">Sub Category Name</Label>
            <Input
              name="subcategory"
              className="py-2"
              value={data.subcategory || ""}
              onChange={handleChange}
              placeholder="e.g. Soft Drinks, Laptop"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border-0 pt-0">
          <Button color="light" className="px-4" onClick={() => { resetModal(); setSubCatModal(false); }}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            disabled={loading} 
            onClick={submitSubCategory} 
            className="px-4 d-flex align-items-center gap-2 shadow-sm"
          >
            {loading && <Spinner size="sm" color="light" />}
            {isEdit && editType === "subcategory" ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Add;